import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHttpClient } from "shared/hooks/http-hook";
import { useForm } from "shared/hooks/form-hook";
import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import FormFooter from "shared/components/FormElements/FormFooter";
import GenericCard from "components/ui/cards/GenericCard";
import NotFound from "shared/components/UIElements/NotFound";
import ItemGenerated from "components/shared/ItemGenerated";
import DeleteModal from "components/shared/DeleteModal";
import { StoreContext } from "shared/context/store-context";

import "./InventoryStockItem.css";

const InventoryStockItem = ({ itemId, isNewItem, DeleteItem, InsertItem, UpdateItem, onClose, gotoItem }) => {
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { stocks } = useContext(StoreContext);

  // Depo options constant
  const depoOptions = [
    { value: "spare_parts", label: t("inventoryStock.depo.spare_parts") },
    { value: "fuel", label: t("inventoryStock.depo.fuel") },
    { value: "maintenance", label: t("inventoryStock.depo.maintenance") },
    { value: "electronics", label: t("inventoryStock.depo.electronics") },
    { value: "consumables", label: t("inventoryStock.depo.consumables") },
    { value: "tools", label: t("inventoryStock.depo.tools") },
    { value: "documents", label: t("inventoryStock.depo.documents") },
    { value: "other", label: t("inventoryStock.depo.other") },
  ];

  // Form states using useForm hook
  const [formState, inputHandler] = useForm(
    {
      name: { value: "", isValid: false },
      description: { value: "", isValid: true },
      depo: { value: "other", isValid: true },
      notes: { value: "", isValid: true },
      currentQuantity: { value: 0, isValid: true },
      unit: { value: "piece", isValid: true },
      unitPrice: { value: 0, isValid: true },
    },
    false
  );

  // Additional states

  // Fetch item data
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentItem = stocks.find((item) => item.id === itemId);
      setLoadedItem(currentItem);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [stocks, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // Stock units and categories
  const stockUnits = [
    { value: "piece", label: t("inventoryStock.unitOptions.piece") },
    { value: "liter", label: t("inventoryStock.unitOptions.liter") },
    { value: "kilogram", label: t("inventoryStock.unitOptions.kilogram") },
    { value: "meter", label: t("inventoryStock.unitOptions.meter") },
    { value: "square_meter", label: t("inventoryStock.unitOptions.square_meter") },
    { value: "cubic_meter", label: t("inventoryStock.unitOptions.cubic_meter") },
    { value: "box", label: t("inventoryStock.unitOptions.box") },
    { value: "package", label: t("inventoryStock.unitOptions.package") },
    { value: "set", label: t("inventoryStock.unitOptions.set") },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        name: formState.inputs.name.value.toString().trim(),
        description: formState.inputs.description.value,
        depo: formState.inputs.depo.value,
        notes: formState.inputs.notes.value,
        currentQuantity: parseFloat(formState.inputs.currentQuantity.value) || 0,
        unit: formState.inputs.unit.value,
        unitPrice: parseFloat(formState.inputs.unitPrice.value) || 0,
      };

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/stock`
        : `${process.env.REACT_APP_BACKEND_URL}/stock/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.stock) {
        if (isNewItem) {
          InsertItem(responseData.stock);
          setGeneratedItemId(responseData.stock.id);
        } else {
          UpdateItem(responseData.stock);
          window.toast.success(t("inventoryStock.messages.updateSuccess"));
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(`${t("inventoryStock.messages.error")}: ${err.code}`);
      } else {
        window.toast.error(t("inventoryStock.messages.operationFailed"));
      }
    }
  };

  // Item not found check
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item="stock" />;
  }

  // Item generated success page
  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType="stock"
        onGotoNewItem={() => gotoItem && gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose && onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint="stock"
        itemType="stock"
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={handleSubmit}>
          {/* Basic Information Card */}
          <GenericCard style={{ marginBottom: "10px" }}>
            <div className="horizontal-flex">
              {!isNewItem && (
                <Input
                  id="stockNumber"
                  element="input"
                  type="text"
                  label={t("inventoryStock.fields.stockNumber")}
                  validators={[VALIDATOR_NONE()]}
                  onInput={inputHandler}
                  initialValue={loadedItem?.stockNumber || ""}
                  initialValid={true}
                  disabled={true}
                />
              )}

              <Input
                id="name"
                element="input"
                type="text"
                label={t("inventoryStock.fields.nameRequired")}
                validators={[VALIDATOR_REQUIRE()]}
                errorText={t("inventoryStock.errors.nameRequired")}
                onInput={inputHandler}
                initialValue={loadedItem?.name || ""}
                initialValid={!isNewItem}
                placeholder={t("inventoryStock.placeholders.name")}
              />

              <CustomSelect
                id="depo"
                label={t("inventoryStock.fields.depoRequired")}
                options={depoOptions}
                onInput={inputHandler}
                initialValue={{
                  value: loadedItem?.depo || "other",
                  label:
                    depoOptions.find((d) => d.value === (loadedItem?.depo || "other"))?.label ||
                    t("inventoryStock.depo.other"),
                }}
              />
            </div>

            <Input
              id="description"
              element="textarea"
              label={t("inventoryStock.fields.description")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.description || ""}
              initialValid={true}
              placeholder={t("inventoryStock.placeholders.description")}
              rows="3"
            />

            <Input
              id="notes"
              element="textarea"
              label={t("inventoryStock.fields.notes")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.notes || ""}
              initialValid={true}
              placeholder={t("inventoryStock.placeholders.notes")}
              rows="2"
            />
          </GenericCard>

          {/* Quantity & Price Information Card */}
          <GenericCard style={{ marginBottom: "10px" }}>
            <div className="horizontal-flex">
              <Input
                id="currentQuantity"
                element="input"
                type="number"
                label={t("inventoryStock.fields.currentQuantity")}
                validators={[VALIDATOR_NONE()]}
                onInput={inputHandler}
                initialValue={loadedItem?.currentQuantity || 0}
                initialValid={true}
                step="0.01"
                min="0"
              />

              <CustomSelect
                id="unit"
                label={t("inventoryStock.fields.unitRequired")}
                options={stockUnits}
                onInput={inputHandler}
                initialValue={{
                  value: loadedItem?.unit || "piece",
                  label:
                    stockUnits.find((u) => u.value === (loadedItem?.unit || "piece"))?.label ||
                    t("inventoryStock.unitOptions.piece"),
                }}
              />

              <Input
                id="unitPrice"
                element="input"
                type="number"
                label={t("inventoryStock.fields.unitPrice")}
                validators={[VALIDATOR_NONE()]}
                onInput={inputHandler}
                initialValue={loadedItem?.unitPrice || 0}
                initialValid={true}
                step="0.01"
                min="0"
              />

              {!isNewItem && (
                <div>
                  <label style={{ fontWeight: "bold", color: "#495057", fontSize: "14px", minWidth: "100px" }}>
                    {t("inventoryStock.fields.totalValue")}
                  </label>
                  <div className="calculated-value">
                    ₺
                    {(
                      parseFloat(formState.inputs.currentQuantity.value) * parseFloat(formState.inputs.unitPrice.value)
                    ).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </GenericCard>

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={handleSubmit}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={isLoading || !formState.isValid}
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryStockItem;
